import { ComponentChildren, ComponentChild } from 'preact';
import { useCallback } from 'preact/hooks';
import { classNames } from '../utils/UNSAFE_classNames';
import { usePress } from '../hooks/UNSAFE_usePress';
import { useUser } from '../hooks/UNSAFE_useUser';
import { Text } from '../UNSAFE_Text';
import { Flex } from '../UNSAFE_Flex';
import { useTabBarContext } from './useTabBarContext';
import { Size } from '../utils/UNSAFE_size';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { flexitemInterpolations } from '../utils/UNSAFE_interpolations/flexitem';
import { dimensionInterpolations } from '../utils/UNSAFE_interpolations/dimensions';
import type { FlexitemProps } from '../utils/UNSAFE_interpolations/flexitem';
import type { DimensionProps } from '../utils/UNSAFE_interpolations/dimensions';
import type { BoxAlignmentProps } from '../utils/UNSAFE_interpolations/boxalignment';
import { boxAlignmentInterpolations } from '../utils/UNSAFE_interpolations/boxalignment';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '../resources/nls/bundle';
import { TabBarItemVariantOptions } from './themes/TabBarItemStyles.css';
import { TabBarItemRedwoodTheme } from './themes/redwood/TabBarItemTheme';
import { Badge } from '../UNSAFE_Badge';
import { useConveyorBeltItem } from '../UNSAFE_ConveyorBelt/useConveyorBeltItem';
import { useDetectTruncation } from '../hooks/PRIVATE_useDetectTruncation';
import { WarningS } from '../UNSAFE_RedwoodIcons/WarningS';
import { ErrorS } from '../UNSAFE_RedwoodIcons/ErrorS';
import { InformationS } from '../UNSAFE_RedwoodIcons/InformationS';
import { SuccessS } from '../UNSAFE_RedwoodIcons/SuccessS';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { useTabBarItemTooltip } from './useTabBarItemTooltip';
import { tabBarItemVars } from './themes/TabBarItemContract.css';
import { HiddenAccessible } from '#UNSAFE_HiddenAccessible';

type Severity = 'warning' | 'info' | 'none' | 'error' | 'confirmation';

type AriaProps = {
  /**
   * Accepts the tabpanel element's ID associated with the tab item
   **/
  'aria-controls'?: string;
  /**
   * Used by an item that renders an interactive popup
   */
  'aria-haspopup'?: boolean;
};

export type BaseTabBarItemProps<K extends string | number> = {
  /**
   * Key of the TabBarItem
   */
  itemKey: K;
  /**
   * Label of the TabBarItem. For icon only display this label is the content
   * for aria-label and tooltip text of the Tab.
   */
  label: string;
  /**
   * The icon before the label in non-stack case, icon above the label in
   * stacked case or the stand alone icon when no label is specified.
   */
  icon?: ComponentChild;
  /**
   * The content to be rendered inside the Badge component.
   */
  badge?: string;
  /**
   * The content to be rendered inside the Text component as a metadata,
   * that appears after the label in non stack case only.
   */
  metadata?: string;
  /**
   * The status icon to be rendered after the label in non stack case only.
   */
  severity?: Severity;
  /**
   * The content to be rendered for remove icon
   */
  removeIcon?: ComponentChildren;
  /**
   * The control for enabling and disabling focusRing
   */
  showFocusRingOverride?: boolean;
  /**
   * The control for enabling and disabling selection indicator
   */
  selectionOverride?: boolean;
  /**
   * The control for overriding display value.
   */
  displayOverride?: string;
  /**
   * Maximum width tabbar item label can have
   */
  labelMaxWidth?: Size;
  /**
   * Sets the URL that the hyperlink points to. If there is no valid URL use "#" for href value to navigate to the top of the page.
   */
  href?: string;
} & AriaProps;

const onClickLink = (event: MouseEvent) => {
  event.preventDefault();
};

export function BaseTabBarItem<K extends string | number>({
  itemKey,
  label,
  icon,
  badge,
  metadata,
  severity = 'none',
  removeIcon,
  showFocusRingOverride,
  selectionOverride,
  displayOverride,
  labelMaxWidth,
  href,
  'aria-controls': ariaControls,
  'aria-haspopup': ariaHasPopup
}: BaseTabBarItemProps<K>) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const {
    onSelect,
    selection,
    currentKey,
    showFocusRing,
    hideTooltip,
    layout,
    display,
    size,
    isEdgeBottom,
    tabItemPrefix
  } = useTabBarContext();

  const isCurrent = currentKey === itemKey;

  const { ref, ...conveyorBeltProps } = useConveyorBeltItem({ isCurrent });

  //Selection
  //Dependency on currentKey because upon removal selectionChange does not change hence no re render
  //and it is still holding the deleted key as selection
  const handlePress = useCallback(() => {
    onSelect?.({ value: itemKey as K });
  }, [itemKey, onSelect]);

  const { pressProps } = usePress(handlePress);

  const isSelected = selection === itemKey;

  const isStacked = display === 'stacked' ? true : false;
  const isIconOnly =
    display === 'icons' || (displayOverride && displayOverride === 'icons') ? true : false;
  const isTall = size === 'lg' ? true : false;
  const labelRequirement = label && !(label === ' ' && label === undefined);
  const severityRequirement = !(severity === 'none' || severity === undefined);

  const IconComponent = (severityValue: Severity) => {
    switch (severityValue) {
      case 'error':
        // As per spec acc label is required only for error severity icon.
        return <ErrorS color="danger" accessibleLabel={translations.tabBar_tabContainsErrors()} />;
      case 'warning':
        return <WarningS color="warning" />;
      case 'info':
        return <InformationS color="info" />;
      case 'confirmation':
        return <SuccessS color="success" />;
      default:
        return null;
    }
  };

  const { direction } = useUser();
  const makeFocusRingVisible =
    showFocusRingOverride !== undefined ? showFocusRingOverride : showFocusRing;
  const makeSelectionVisible = selectionOverride !== undefined ? selectionOverride : isSelected;
  const {
    classes,
    styles: {
      tabBarItemIcon,
      tabBarItemIconStacked,
      tabBarItemMetadata,
      tabBarItemStackedBadge,
      tabBarItemStackedBadgeRtl,
      tabBarItemRemoveIcon,
      tabBarItemLabel,
      tabBarItemLabelTruncated,
      tabBarItemLink
    }
  } = useComponentTheme<TabBarItemVariantOptions>(TabBarItemRedwoodTheme, {
    selection: makeSelectionVisible ? 'isSelected' : 'notSelected',
    current: isCurrent ? 'isCurrent' : 'notCurrent',
    focusRing: makeFocusRingVisible ? 'showFocusRing' : 'noFocusRing',
    edgeBottom: isEdgeBottom ? 'isBottom' : 'notBottom'
  });

  const tabBarItem = classNames([classes]);
  const tabBarItemLabelClasses = classNames([
    tabBarItemLabel,
    labelMaxWidth !== undefined && tabBarItemLabelTruncated
  ]);
  const tabBarItemIconClasses = classNames([
    tabBarItemIcon,
    (isStacked || isIconOnly || !labelRequirement) && tabBarItemIconStacked
  ]);
  const tabBarItemMetadataClasses = classNames([tabBarItemMetadata]);
  const tabBarItemStackedBadgeClasses = classNames([
    tabBarItemStackedBadge,
    direction === 'rtl' && tabBarItemStackedBadgeRtl
  ]);
  const removeClass = classNames([tabBarItemRemoveIcon]);
  const tabBarItemLinkClasses = classNames([tabBarItemLink]);

  const styleInterpolations = mergeInterpolations<
    FlexitemProps & DimensionProps & BoxAlignmentProps
  >([
    ...Object.values(flexitemInterpolations),
    ...Object.values(dimensionInterpolations),
    ...Object.values(boxAlignmentInterpolations)
  ]);

  //This is for simple item where height and width need to be specified on the main div.
  const tabDimensions = styleInterpolations({
    flex: layout === 'stretch' ? '1 0 auto' : '0 0 auto',
    minWidth: '10.5x',
    height: isTall ? tabBarItemVars.lgHeight : tabBarItemVars.mdHeight,
    //This is needed for gap between icon/ label and remove icon in labelIconRenderer
    gap: '2x'
  });

  //This is for complex item, where only flex dimensions needs to be applied as height and width comes from child Flex component.
  const flexDimensions = styleInterpolations({
    flex: layout === 'stretch' ? '1 0 auto' : '0 0 auto'
  });

  const maxWithInterpolation = mergeInterpolations<DimensionProps>([
    ...Object.values(dimensionInterpolations)
  ]);

  //The threshold for truncation is labelMinWidth: 96px as per spec.
  const labelMinWidth = '96px';
  const maxWidthDimensions = maxWithInterpolation({
    maxWidth: labelMaxWidth !== undefined ? `calc(max(${labelMinWidth},${labelMaxWidth}))` : '100%'
  });

  const { detectTruncationProps, isTextTruncated } = useDetectTruncation(
    labelMaxWidth === undefined
  );
  const showTooltip = isIconOnly || isTextTruncated;
  const { tooltipContent, tooltipEventHandlerProps, onLogicalFocus, onLogicalBlur } =
    useTabBarItemTooltip({ label, isDisabled: !showTooltip });
  if (showTooltip) {
    if (isCurrent && showFocusRing && !hideTooltip) {
      onLogicalFocus(ref.current as HTMLElement);
    } else if (!isCurrent || (isCurrent && !showFocusRing) || hideTooltip) {
      onLogicalBlur();
    }
  }

  const isComplexItem = badge || metadata || severity !== 'none' || isStacked || isIconOnly;
  const isLinkItem = href != null;

  const labelIconRenderer = () => {
    return (
      <>
        <Flex align="center" justify="center" flex="1 1 auto" gap="2x">
          {!isIconOnly && !isStacked && icon && labelRequirement && (
            <span class={tabBarItemIconClasses} data-oj-tab-icon="startIcon">
              {icon}
            </span>
          )}
          {!isIconOnly && labelRequirement && (
            <span
              class={tabBarItemLabelClasses}
              style={maxWidthDimensions}
              {...detectTruncationProps}>
              {
                <Text size="md" weight="semiBold">
                  {label}
                </Text>
              }
            </span>
          )}
        </Flex>
        {!isLinkItem && removeIcon && <span class={removeClass}>{removeIcon}</span>}
      </>
    );
  };

  const complexItemRenderer = () => {
    return (
      <Flex flex="1 1 auto">
        <Flex
          minWidth="10.5x"
          minHeight={isTall ? tabBarItemVars.lgHeight : '100%'}
          height={
            isStacked && labelRequirement ? tabBarItemVars.stackedHeight : tabBarItemVars.mdHeight
          }
          direction={isStacked && labelRequirement ? 'column' : 'row'}
          gap="2x"
          align="center"
          justify="center"
          alignSelf="center"
          flex="1 1 auto">
          {(isStacked || isIconOnly) && (
            <div class={tabBarItemIconClasses}>
              {
                // JET-79198 - The label is made hidden so that in can be read out by screen readers and we will not need aria-label at role="tab" level to include all the content in it's children
                isIconOnly && <HiddenAccessible id="hiddenLabel">{label}</HiddenAccessible>
              }
              {icon}
              <span class={tabBarItemStackedBadgeClasses}>
                {badge ? (
                  <Badge size="xs" variant="neutral">
                    {badge}
                  </Badge>
                ) : null}
              </span>
            </div>
          )}
          {!isIconOnly && !isStacked && icon && labelRequirement && (
            <span class={tabBarItemIconClasses} data-oj-tab-icon="startIcon">
              {icon}
            </span>
          )}
          {!isIconOnly && labelRequirement && (
            <span
              class={tabBarItemLabelClasses}
              style={maxWidthDimensions}
              {...detectTruncationProps}>
              {
                <Text size="md" weight="semiBold">
                  {label}
                </Text>
              }
            </span>
          )}
          {!isStacked && !isIconOnly && badge && labelRequirement && (
            <span data-oj-tab-badge={badge}>
              <Badge size="md" variant="neutralSubtle">
                {badge}
              </Badge>
            </span>
          )}
          {metadata && (
            <span class={tabBarItemMetadataClasses} data-oj-tab-metadata={metadata}>
              <Text size="md" weight="normal">
                {'( ' + metadata + ' )'}
              </Text>
            </span>
          )}
          {severityRequirement && (
            <span class={tabBarItemIconClasses} data-oj-tab-severity={severity}>
              {IconComponent(severity)}
            </span>
          )}
        </Flex>
        {!isLinkItem && removeIcon && <span class={removeClass}>{removeIcon}</span>}
      </Flex>
    );
  };

  const tabBarItemRenderer = () => {
    return (
      <div
        data-oj-key={itemKey}
        data-oj-removable={removeIcon ? 'true' : undefined}
        id={tabItemPrefix + itemKey}
        class={tabBarItem}
        role="tab"
        aria-controls={ariaControls}
        aria-selected={isSelected}
        aria-haspopup={ariaHasPopup}
        {...mergeProps(pressProps, tooltipEventHandlerProps, conveyorBeltProps)}
        ref={ref}
        style={isComplexItem ? flexDimensions : tabDimensions}>
        {isComplexItem ? complexItemRenderer() : labelIconRenderer()}
        {tooltipContent}
      </div>
    );
  };

  const tabBarLinkItemRenderer = () => {
    return (
      <span
        ref={ref}
        class={tabBarItem}
        {...mergeProps(pressProps, tooltipEventHandlerProps, conveyorBeltProps)}
        style={isComplexItem ? flexDimensions : tabDimensions}>
        <a
          onClick={onClickLink}
          class={tabBarItemLinkClasses}
          href={href}
          data-oj-key={itemKey}
          data-oj-removable={removeIcon ? 'true' : undefined}
          id={tabItemPrefix + itemKey}
          role="tab"
          tabindex={-1}
          aria-controls={ariaControls}
          aria-selected={isSelected}
          aria-haspopup={ariaHasPopup}>
          {isComplexItem ? complexItemRenderer() : labelIconRenderer()}
        </a>
        {tooltipContent}
      </span>
    );
  };
  return isLinkItem ? tabBarLinkItemRenderer() : tabBarItemRenderer();
}
